import React, { createContext, useCallback, useEffect, useState } from "react";
import axios from "axios";
import LoadingComponent from "../components/general/LoadingComponent";
import { useParams } from "react-router-dom";
import { ContractState } from "../enums/ContractState";

export const ContractContext = createContext(null);

const READY_CONTRACT_STATES = [
    ContractState.LLM_CLASSIFICATION_DONE,
    ContractState.FINISHED_REVIEW,
    ContractState.LLM_RECTIFICATION_DONE,
    ContractState.FINISHED
];

function updateClauseInContract(contract, clauseId, updateClause) {
    if (!contract) return contract;

    return {
        ...contract,
        paragraphs: contract.paragraphs.map(paragraph => ({
            ...paragraph,
            clauses: paragraph.clauses.map(clause =>
                clause._id === clauseId ? updateClause(clause) : clause
            )
        }))
    };
}

const ContractProvider = ({ children }) => {
    const { contractId } = useParams();
    const [contract, setContract] = useState(null);

    const fetchContract = useCallback(async () => {
        try {
            const response = await axios.get(`/contracts/${contractId}`);
            setContract(response.data);
        } catch (error) {
            console.error(error);
            setContract(null);
        }
    }, [contractId]);

    useEffect(() => {
        let timeoutId;

        if (!contract || contract._id !== contractId) {
            fetchContract();
        } else if (!READY_CONTRACT_STATES.includes(contract.state)) {
            timeoutId = setTimeout(fetchContract, 2000);
        }

        return () => {
            if (timeoutId) clearTimeout(timeoutId);
        };
    }, [contract, fetchContract]);

    const runAndRefresh = useCallback(async (operation, errorMessage) => {
        try {
            await operation();
            await fetchContract();
        } catch (error) {
            console.error(errorMessage, error);
            await fetchContract();
        }
    }, [fetchContract]);

    const runLLM = async () => runAndRefresh(
        () => axios.patch(`/contracts/runLLM/${contractId}`),
        "Error running LLM classification:"
    );

    const resetContract = async (newState) => {
        try {
            await axios.patch(`/contracts/reset/${contractId}`, {targetState: newState});
            setContract(prevContract => prevContract
                ? {...prevContract, state: newState}
                : prevContract
            );
        } catch (error) {
            console.error("Error resetting contract:", error);
            await fetchContract();
        }
    };

    const addReview = async (newReview) => {
        try {
            const response = await axios.post(`/reviews/`, newReview);
            const createdReview = response.data;

            setContract(prevContract => updateClauseInContract(
                prevContract,
                createdReview.clause,
                clause => ({
                    ...clause,
                    reviews: [...(clause.reviews || []), createdReview]
                })
            ));
        } catch (error) {
            console.error("Error adding review:", error);
            await fetchContract();
        }
    };

    const updateCorrection = async (correctionId, newText) => {
        try {
            const response = await axios.put(`/corrections/${correctionId}`, { text: newText });
            const updatedCorrection = response.data;

            setContract(prevContract => updateClauseInContract(
                prevContract,
                updatedCorrection.clause,
                clause => ({
                    ...clause,
                    correction: updatedCorrection
                })
            ));
        } catch (error) {
            console.error("Error updating correction:", error);
            await fetchContract();
        }
    };

    const downgradeParagraph = async (paragraphId) => runAndRefresh(
        () => axios.patch(`/paragraphs/downgrade/${paragraphId}`),
        "Error downgrading paragraph:"
    );

    const changeParagraph = async (paragraphId, updatedContent) => runAndRefresh(
        () => axios.put(`/paragraphs/${paragraphId}`, { content: updatedContent }),
        "Error changing paragraph:"
    );

    const deleteParagraph = async (paragraphId) => runAndRefresh(
        () => axios.delete(`/paragraphs/${paragraphId}`),
        "Error deleting paragraph:"
    );

    const mergeClause = async (clauseId) => runAndRefresh(
        () => axios.patch(`/clauses/merge/${clauseId}`),
        "Error merging clause:"
    );

    const upgradeClause = async (clauseId) => runAndRefresh(
        () => axios.patch(`/clauses/upgrade/${clauseId}`),
        "Error upgrading clause:"
    );

    const changeClause = async (clauseId, updatedContent) => runAndRefresh(
        () => axios.put(`/clauses/${clauseId}`, { content: updatedContent }),
        "Error changing clause:"
    );

    const deleteClause = async (clauseId) => runAndRefresh(
        () => axios.delete(`/clauses/${clauseId}`),
        "Error deleting clause:"
    );

    if (!contract || !READY_CONTRACT_STATES.includes(contract.state)) return <LoadingComponent />;

    return (
        <ContractContext.Provider value={{
            contract,
            runLLM,
            resetContract,
            addReview,
            downgradeParagraph,
            changeParagraph,
            deleteParagraph,
            mergeClause,
            upgradeClause,
            changeClause,
            deleteClause,
            updateCorrection
        }}>
            {children}
        </ContractContext.Provider>
    );
};

export default ContractProvider;
